import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  DialogContent,
  Typography,
  Chip,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import {
  API_get_patterns,
  API_create_pattern,
  API_update_pattern,
  API_delete_pattern,
  API_get_agents,
  API_create_agent,
  API_update_agent,
  API_delete_agent,
} from "./utils/http";
import { AlertCus } from "./components/AlertCus";
import { DialogCus } from "./components/DialogCus";

const btnStyle = {
  borderRadius: "20px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: 13,
};

const sxTextField = {
  m: 0.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
  },
};

const COLUMNS_PATTERNS = [
  { field: "id", headerName: "ID", width: 70 },
  { field: "name", headerName: "Group Name", width: 180 },
  {
    field: "default_response",
    headerName: "Response Template",
    flex: 1,
    minWidth: 250,
    renderCell: (params) => (
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
        title={params.value}
      >
        {params.value}
      </span>
    ),
  },
  {
    field: "patterns",
    headerName: "Patterns",
    width: 100,
    renderCell: (params) => (
      <Chip
        label={params.value ? params.value.length : 0}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    field: "created_at",
    headerName: "Created",
    width: 150,
    renderCell: (params) => {
      if (!params.value) return "";
      let d = new Date(params.value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
];

const COLUMNS_AGENTS = [
  { field: "id", headerName: "ID", width: 70 },
  { field: "agent_id", headerName: "Agent ID (Pancake)", flex: 1, minWidth: 250 },
  { field: "name", headerName: "Name", width: 150 },
  {
    field: "created_at",
    headerName: "Created",
    width: 150,
    renderCell: (params) => {
      if (!params.value) return "";
      let d = new Date(params.value);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
];

export default function PatternResponse() {
  const [tab, setTab] = useState("groups");

  // --- Pattern Groups state ---
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [rowSelection, setRowSelection] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);

  const [formName, setFormName] = useState("");
  const [formDefaultResponse, setFormDefaultResponse] = useState("");
  const [formPatterns, setFormPatterns] = useState("");

  // --- Agents state ---
  const [agents, setAgents] = useState(null);
  const [agentSelectedId, setAgentSelectedId] = useState(null);
  const [agentRowSelection, setAgentRowSelection] = useState([]);

  const [openAgentAdd, setOpenAgentAdd] = useState(false);
  const [openAgentEdit, setOpenAgentEdit] = useState(false);
  const [openAgentDelete, setOpenAgentDelete] = useState(false);

  const [formAgentId, setFormAgentId] = useState("");
  const [formAgentName, setFormAgentName] = useState("");

  // --- Shared status ---
  const [apiResult, setApiResult] = useState(null);
  const [apiMessage, setApiMessage] = useState("");

  // ========== Fetch ==========

  const fetchData = async () => {
    await API_get_patterns({ limit: 1000 }, (res) => {
      setData(res.items || []);
    });
  };

  const fetchAgents = async () => {
    await API_get_agents((res) => {
      setAgents(res.items || []);
    });
  };

  useEffect(() => {
    fetchData();
    fetchAgents();
  }, []);

  // ========== Pattern Groups ==========

  const getSelectedItem = () => {
    if (!data || !selectedId) return null;
    return data.find((item) => item.id === selectedId);
  };

  const openAddDialog = () => {
    setFormName("");
    setFormDefaultResponse("");
    setFormPatterns("");
    setOpenAdd(true);
  };

  const openEditDialog = () => {
    let item = getSelectedItem();
    if (!item) return;
    setFormName(item.name || "");
    setFormDefaultResponse(item.default_response || "");
    setFormPatterns(
      (item.patterns || []).map((p) => p.pattern).join("\n")
    );
    setOpenEdit(true);
  };

  const buildBody = () => {
    let patterns = formPatterns
      .split("\n")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    return {
      name: formName,
      default_response: formDefaultResponse,
      patterns,
    };
  };

  const handleCreate = () => {
    let body = buildBody();
    if (!body.name || !body.default_response) return;
    setOpenAdd(false);
    API_create_pattern(body, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Create");
        fetchData();
      }
    });
  };

  const handleUpdate = () => {
    let body = buildBody();
    setOpenEdit(false);
    API_update_pattern(selectedId, body, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Update");
        fetchData();
      }
    });
  };

  const handleDelete = () => {
    setOpenDelete(false);
    API_delete_pattern(selectedId, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Delete");
        setSelectedId(null);
        setRowSelection([]);
        fetchData();
      }
    });
  };

  // ========== Agents ==========

  const getSelectedAgent = () => {
    if (!agents || !agentSelectedId) return null;
    return agents.find((a) => a.id === agentSelectedId);
  };

  const openAgentAddDialog = () => {
    setFormAgentId("");
    setFormAgentName("");
    setOpenAgentAdd(true);
  };

  const openAgentEditDialog = () => {
    let agent = getSelectedAgent();
    if (!agent) return;
    setFormAgentId(agent.agent_id || "");
    setFormAgentName(agent.name || "");
    setOpenAgentEdit(true);
  };

  const handleAgentCreate = () => {
    if (!formAgentId || !formAgentName) return;
    setOpenAgentAdd(false);
    API_create_agent({ agent_id: formAgentId, name: formAgentName }, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Agent created");
        fetchAgents();
      }
    });
  };

  const handleAgentUpdate = () => {
    setOpenAgentEdit(false);
    API_update_agent(agentSelectedId, { name: formAgentName }, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Agent updated");
        fetchAgents();
      }
    });
  };

  const handleAgentDelete = () => {
    setOpenAgentDelete(false);
    API_delete_agent(agentSelectedId, (res) => {
      if (res.error || res.detail) {
        setApiResult(false);
        setApiMessage(res.error || res.detail);
      } else {
        setApiResult(true);
        setApiMessage("Agent deleted");
        setAgentSelectedId(null);
        setAgentRowSelection([]);
        fetchAgents();
      }
    });
  };

  // ========== Render ==========

  const selectedItem = getSelectedItem();
  const selectedAgent = getSelectedAgent();

  const patternFormFields = (
    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 500 }}>
      <TextField
        label="Group Name"
        value={formName}
        onChange={(e) => setFormName(e.target.value)}
        size="small"
        sx={sxTextField}
        placeholder='e.g. "greeting", "pricing_inquiry"'
      />
      <TextField
        label="Response Template"
        value={formDefaultResponse}
        onChange={(e) => setFormDefaultResponse(e.target.value)}
        multiline
        minRows={3}
        size="small"
        sx={sxTextField}
        helperText="Use {{ADMIN_NAME}} as placeholder for the agent's name"
      />
      <TextField
        label="Patterns (one per line)"
        value={formPatterns}
        onChange={(e) => setFormPatterns(e.target.value)}
        multiline
        minRows={3}
        size="small"
        sx={sxTextField}
        placeholder={"Can I learn more about your package?\nWhat is the price for solo traveler?"}
      />
    </DialogContent>
  );

  const detailContent = selectedItem ? (
    <DialogContent sx={{ minWidth: 500 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "#64748B" }}>Group Name</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedItem.name}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "#64748B" }}>Response Template</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, borderRadius: "8px", backgroundColor: "#FAFBFC" }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {selectedItem.default_response}
            </Typography>
          </Paper>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "#64748B" }}>
            Patterns ({(selectedItem.patterns || []).length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {(selectedItem.patterns || []).map((p) => (
              <Chip key={p.id} label={p.pattern} size="small" variant="outlined" />
            ))}
            {(selectedItem.patterns || []).length === 0 && (
              <Typography variant="body2" sx={{ color: "#94A3B8", fontStyle: "italic" }}>
                No patterns (default greeting group)
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </DialogContent>
  ) : null;

  const agentFormFields = (
    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 400 }}>
      <TextField
        label="Agent ID (from Pancake)"
        value={formAgentId}
        onChange={(e) => setFormAgentId(e.target.value)}
        size="small"
        sx={sxTextField}
        placeholder="8cf1750c-f921-47d4-ac57-a8cef40fc7ba"
        disabled={openAgentEdit}
      />
      <TextField
        label="Agent Name"
        value={formAgentName}
        onChange={(e) => setFormAgentName(e.target.value)}
        size="small"
        sx={sxTextField}
        placeholder='e.g. "Amy"'
      />
    </DialogContent>
  );

  const gridSx = {
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#F8FAFC",
    },
  };

  const subToggleSx = {
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 500,
      fontSize: 12,
      px: 2,
      py: 0.4,
      borderRadius: "16px !important",
      border: "1px solid #E0E0E0 !important",
      mx: 0.3,
      "&.Mui-selected": {
        backgroundColor: "#E3F2FD",
        color: "#1565C0",
        borderColor: "#1565C0 !important",
        "&:hover": { backgroundColor: "#BBDEFB" },
      },
    },
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
      <AlertCus condition={apiResult} message={apiMessage} error_text={apiMessage} />

      {/* Sub-tab toggle */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={(_, v) => { if (v) setTab(v); }}
          size="small"
          sx={subToggleSx}
        >
          <ToggleButton value="groups">
            <FormatListBulletedRoundedIcon sx={{ fontSize: 15, mr: 0.5 }} />
            Pattern Groups
          </ToggleButton>
          <ToggleButton value="agents">
            <PersonRoundedIcon sx={{ fontSize: 15, mr: 0.5 }} />
            Agents
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* ===== Pattern Groups Tab ===== */}
      {tab === "groups" && (
        <>
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={openAddDialog}
              sx={{ ...btnStyle, backgroundColor: "#1565C0" }}
            >
              Add Group
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={openEditDialog}
              disabled={!selectedId}
              sx={btnStyle}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => setOpenDelete(true)}
              disabled={!selectedId}
              sx={btnStyle}
            >
              Delete
            </Button>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={data || []}
              columns={COLUMNS_PATTERNS}
              density="comfortable"
              rowSelectionModel={rowSelection}
              onRowSelectionModelChange={(ids) => {
                setRowSelection(ids);
                setSelectedId(ids.length > 0 ? ids[0] : null);
              }}
              onRowDoubleClick={(params) => {
                setSelectedId(params.id);
                setOpenDetail(true);
              }}
              disableMultipleRowSelection
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={gridSx}
            />
          </Box>
        </>
      )}

      {/* ===== Agents Tab ===== */}
      {tab === "agents" && (
        <>
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={openAgentAddDialog}
              sx={{ ...btnStyle, backgroundColor: "#1565C0" }}
            >
              Add Agent
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={openAgentEditDialog}
              disabled={!agentSelectedId}
              sx={btnStyle}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => setOpenAgentDelete(true)}
              disabled={!agentSelectedId}
              sx={btnStyle}
            >
              Delete
            </Button>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={agents || []}
              columns={COLUMNS_AGENTS}
              density="comfortable"
              rowSelectionModel={agentRowSelection}
              onRowSelectionModelChange={(ids) => {
                setAgentRowSelection(ids);
                setAgentSelectedId(ids.length > 0 ? ids[0] : null);
              }}
              disableMultipleRowSelection
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={gridSx}
            />
          </Box>
        </>
      )}

      {/* ===== Pattern Dialogs ===== */}
      <DialogCus
        title="Add Pattern Group"
        open={openAdd}
        handleClickOpen={() => setOpenAdd(false)}
        handleClickConfirm={handleCreate}
      >
        {patternFormFields}
      </DialogCus>

      <DialogCus
        title="Edit Pattern Group"
        open={openEdit}
        handleClickOpen={() => setOpenEdit(false)}
        handleClickConfirm={handleUpdate}
      >
        {patternFormFields}
      </DialogCus>

      <DialogCus
        title={`Delete group "${selectedItem?.name || ""}"?`}
        open={openDelete}
        handleClickOpen={() => setOpenDelete(false)}
        handleClickConfirm={handleDelete}
      >
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#64748B" }}>
            This will permanently delete the group and all its patterns.
          </Typography>
        </DialogContent>
      </DialogCus>

      <DialogCus
        title={"Group: " + (selectedItem?.name || "")}
        open={openDetail}
        handleClickOpen={() => setOpenDetail(false)}
        handleClickConfirm={null}
      >
        {detailContent}
      </DialogCus>

      {/* ===== Agent Dialogs ===== */}
      <DialogCus
        title="Add Agent"
        open={openAgentAdd}
        handleClickOpen={() => setOpenAgentAdd(false)}
        handleClickConfirm={handleAgentCreate}
      >
        {agentFormFields}
      </DialogCus>

      <DialogCus
        title="Edit Agent"
        open={openAgentEdit}
        handleClickOpen={() => setOpenAgentEdit(false)}
        handleClickConfirm={handleAgentUpdate}
      >
        {agentFormFields}
      </DialogCus>

      <DialogCus
        title={`Delete agent "${selectedAgent?.name || ""}"?`}
        open={openAgentDelete}
        handleClickOpen={() => setOpenAgentDelete(false)}
        handleClickConfirm={handleAgentDelete}
      >
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#64748B" }}>
            This will remove the agent name mapping. The default name "Emily" will be used instead.
          </Typography>
        </DialogContent>
      </DialogCus>
    </Box>
  );
}
